import { useCallback, useEffect, useMemo, useState } from "react";

import { useUpdateListPersonMutation } from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../../List/schema/list";
import { CopywritingCampaign } from "../../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { resolveSelectedCampaignConfig } from "../../utils/resolveSelectedCampaignConfig";

type SelectedCampaignConfig = (CopywritingCampaign & { isCustom: boolean }) | null | undefined;

interface UseOutboxCampaignConfigArgs {
	inbox: ListPerson[];
	currentLeadIndex: number;
}

interface OutboxCampaignConfig {
	/** The sequence shown for the current lead — the list default or the lead's own override. */
	selectedCampaignConfig: SelectedCampaignConfig;
	/** The in-progress edit of that sequence; `null` until the user changes something. */
	customCampaignConfig: CopywritingCampaign | null;
	setCustomCampaignConfig: (config: CopywritingCampaign | null) => void;
	isDirty: boolean;
	setIsDirty: (isDirty: boolean) => void;
	isValidCustomCampaignConfig: boolean;
	isUpdatingListPerson: boolean;
	/** One-tick remount guard so the sequence editor rebuilds against the new lead. */
	isLoading: boolean;
	handleSaveCustom: (campaignConfig: CopywritingCampaign | null) => Promise<void>;
}

/**
 * Owns the campaign sequence the Outbox's right pane edits: which config the current lead
 * resolves to, the unsaved edit on top of it, and persisting that edit back onto the lead.
 * Extracted from `Outbox.tsx` so the page keeps only its layout and request wiring.
 */
export const useOutboxCampaignConfig = ({
	inbox,
	currentLeadIndex,
}: UseOutboxCampaignConfigArgs): OutboxCampaignConfig => {
	const [updateListPerson, { isLoading: isUpdatingListPerson }] = useUpdateListPersonMutation();

	const [customCampaignConfig, setCustomCampaignConfig] = useState<CopywritingCampaign | null>(
		null
	);

	const [selectedCampaignConfig, setSelectedCampaignConfig] =
		useState<SelectedCampaignConfig>(null);

	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const resetDefaultCampaignConfig = useCallback(() => {
		setIsDirty(false);

		if (currentLeadIndex === undefined || currentLeadIndex < 0) {
			return setSelectedCampaignConfig(undefined);
		}

		const prospect = inbox[currentLeadIndex];

		setSelectedCampaignConfig(prospect ? resolveSelectedCampaignConfig(prospect) : undefined);
	}, [inbox, currentLeadIndex]);

	useEffect(() => {
		resetDefaultCampaignConfig();
	}, [inbox, currentLeadIndex]);

	const isValidCustomCampaignConfig = useMemo(() => {
		if (!customCampaignConfig) {
			return false;
		}

		return customCampaignConfig.isValid ?? false;
	}, [customCampaignConfig]);

	useEffect(() => {
		setIsLoading(true);

		setTimeout(() => {
			setIsLoading(false);
		}, 1);
	}, [currentLeadIndex, selectedCampaignConfig]);

	const handleSaveCustom = useCallback(
		async (campaignConfig: CopywritingCampaign | null) => {
			await updateListPerson({
				id: inbox[currentLeadIndex]?.id,
				campaignConfig: campaignConfig
					? JSON.stringify({
							...campaignConfig,
						})
					: null,
			}).unwrap();

			setIsDirty(false);

			if (campaignConfig === null) {
				setSelectedCampaignConfig(null);
			}
		},
		[updateListPerson, inbox, currentLeadIndex]
	);

	return {
		selectedCampaignConfig,
		customCampaignConfig,
		setCustomCampaignConfig,
		isDirty,
		setIsDirty,
		isValidCustomCampaignConfig,
		isUpdatingListPerson,
		isLoading,
		handleSaveCustom,
	};
};
